/* eslint-disable jsx-a11y/tabindex-no-positive */
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import api from '~/services/api';
import { useStoreActions, useStoreState } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error/error';
import formatDictionaryOptions from '~/utils/formatDictionaryOptions';

import Button from '~/ui/components/common/Button';
import Loader from '~/ui/components/common/Loader';
import NavigationConfirmModal from '~/ui/components/common/NavigationConfirmModal';
import AvatarUpload from '~/ui/components/inputs/AvatarUpload';
import DatePicker from '~/ui/components/inputs/DatePicker';
import Input from '~/ui/components/inputs/Input';
import InputMask from '~/ui/components/inputs/InputMask';
import SelectEmailDomain from '~/ui/components/inputs/SelectEmailDomain';
import Select from '~/ui/components/inputs/SelectWithoutAnimation';

import { IDictionaryTypes } from '~/services/api/dictionaries/types';
import { IOption } from '~/types';
import memberOptions, { MemberType } from '~/ui/constants/memberTypes';

import useRole from '~/store/user/hooks/useRole';
import { IFormValues } from '../types';
import styles from './Form.module.scss';
import validate from './validate';

interface CancelLinkTo {
  pathname: string;
  state: { selectedTab: number };
}
interface IProps {
  isProcessing: boolean;
  leavePageDescription: string;
  cancelLinkTo: CancelLinkTo;
  emailDomainOptions: IOption[];
  onSubmit: (vals: IFormValues) => void;
}

interface IOptions {
  states: IOption[];
  credentials: IOption[];
  educations: IOption[];
  employmentStatuses: IOption[];
  subRoles: IOption[];
}

const TEAM_IDS_SET_OPTS = { shouldValidate: true, shouldDirty: true } as const;

const Form = ({
  isProcessing,
  leavePageDescription,
  cancelLinkTo,
  emailDomainOptions,
  onSubmit,
}: IProps): ReactElement => {
  const [options, setOptions] = useState<IOptions>(null);
  const [loading, setLoading] = useState(false);

  const showError = useStoreActions(actions => actions.snackbar.showError);
  const onGetAllActTeamsAvailable = useStoreActions(
    actions => actions.actTeam.onGetAllActTeamsAvailable,
  );

  const clinicId = useStoreState(state => state.clinic.current?.id);
  const availableActTeams = useStoreState(state => state.actTeam.allActTeams);

  const teamOptions = useMemo(
    () => formatDictionaryOptions(availableActTeams),
    [availableActTeams],
  );

  const {
    handleSubmit,
    register,
    control,
    setValue,
    unregister,
    watch,
    formState: { errors, isDirty },
  } = useForm<IFormValues>({ mode: 'onBlur', resolver: validate });

  const emailDomain = watch('emailDomain');
  const primaryRole = watch('primaryRole');
  const globalUserSelected = primaryRole === MemberType.GLOBAL_USER;

  const { isLocalAdmin, isProgramAssistant, role, team } = useRole();

  const paAccessibleTeamIds = useMemo(() => {
    const ids = role?.teams?.map(t => t.id) ?? [];
    let raw: number[];
    if (ids.length) {
      raw = ids;
    } else if (team?.id) {
      raw = [team.id];
    } else {
      raw = [];
    }
    const seen = new Set<number>();
    const uniq: number[] = [];
    raw.forEach(id => {
      if (!seen.has(id)) {
        seen.add(id);
        uniq.push(id);
      }
    });
    return uniq.sort((a, b) => a - b);
  }, [role?.teams, team?.id]);

  const paAccessibleTeamSet = useMemo(() => new Set(paAccessibleTeamIds), [paAccessibleTeamIds]);

  const paAccessibleTeamIdsKey = useMemo(
    () => paAccessibleTeamIds.join(','),
    [paAccessibleTeamIds],
  );

  const restrictGuTeamsForPa = Boolean(isProgramAssistant && !isLocalAdmin && globalUserSelected);

  /** PA + GU: default-select accessible teams when options load (re-run if clinic or PA team access changes). */
  const paGuDefaultsFingerprintRef = useRef<string | null>(null);

  const onMount = useCallback(async () => {
    try {
      setLoading(true);
      const [states, credentials, educations, employmentStatuses, subRoles] = await Promise.all([
        api.dictionaries
          .getAvailableTypeList(IDictionaryTypes.State)
          .then(r => formatDictionaryOptions(r.data)),
        api.dictionaries
          .getAvailableTypeList(IDictionaryTypes.Credentials)
          .then(r => formatDictionaryOptions(r.data)),
        api.dictionaries
          .getAvailableTypeList(IDictionaryTypes.Education)
          .then(r => formatDictionaryOptions(r.data)),
        api.dictionaries
          .getAvailableTypeList(IDictionaryTypes.EmploymentStatus)
          .then(r => formatDictionaryOptions(r.data)),
        api.dictionaries
          .getAvailableTypeList(IDictionaryTypes.SubRole)
          .then(r => formatDictionaryOptions(r.data)),
      ]);

      setOptions({ states, credentials, educations, employmentStatuses, subRoles });
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  // Global User: clinic act-team/available-list (scoped to what the editor may assign; PA selects teams here).
  const fetchAvailableTeams = useCallback(async () => {
    if (!globalUserSelected || !clinicId) return;

    try {
      await onGetAllActTeamsAvailable({ clinicId: String(clinicId) });
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  }, [clinicId, globalUserSelected, onGetAllActTeamsAvailable, showError]);

  useEffect(() => {
    fetchAvailableTeams();
  }, [fetchAvailableTeams]);

  useEffect(() => {
    if (emailDomainOptions.length && !emailDomain) {
      setValue('emailDomain', String(emailDomainOptions[0].value));
    }
  }, [emailDomainOptions, emailDomain, setValue]);

  const setAll = useCallback(
    (all?: boolean) => {
      if (!availableActTeams?.length) return;
      const availableIdSet = new Set(availableActTeams.map(t => t.id));
      if (all) {
        if (restrictGuTeamsForPa) {
          const accessibleInList = Array.from(paAccessibleTeamSet).filter(id =>
            availableIdSet.has(id),
          );
          setValue('teamIds', accessibleInList, TEAM_IDS_SET_OPTS);
        } else {
          setValue(
            'teamIds',
            availableActTeams.map(m => m.id),
            TEAM_IDS_SET_OPTS,
          );
        }
      } else {
        setValue('teamIds', [], TEAM_IDS_SET_OPTS);
      }
    },
    [availableActTeams, paAccessibleTeamSet, restrictGuTeamsForPa, setValue],
  );

  useEffect(() => {
    if (!globalUserSelected) {
      paGuDefaultsFingerprintRef.current = null;
      unregister(['teamIds']);
      return;
    }

    setValue('teamIds', [], TEAM_IDS_SET_OPTS);
  }, [globalUserSelected, setValue, unregister]);

  useEffect(() => {
    if (!globalUserSelected || !restrictGuTeamsForPa || !clinicId || !availableActTeams?.length) {
      return;
    }

    if (!paAccessibleTeamIdsKey) {
      return;
    }

    const fingerprint = `${clinicId}:${paAccessibleTeamIdsKey}`;
    if (paGuDefaultsFingerprintRef.current === fingerprint) {
      return;
    }

    const availableIdSet = new Set(availableActTeams.map(t => t.id));
    const defaultIds = paAccessibleTeamIds.filter(id => availableIdSet.has(id));

    setValue('teamIds', defaultIds, TEAM_IDS_SET_OPTS);
    paGuDefaultsFingerprintRef.current = fingerprint;
  }, [
    availableActTeams,
    clinicId,
    globalUserSelected,
    paAccessibleTeamIds,
    paAccessibleTeamIdsKey,
    restrictGuTeamsForPa,
    setValue,
  ]);

  const isTeamOptionDisabledForPa = useCallback(
    (option: IOption) => restrictGuTeamsForPa && !paAccessibleTeamSet.has(Number(option.value)),
    [paAccessibleTeamSet, restrictGuTeamsForPa],
  );

  if (loading || !options) return <Loader />;

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.formContainer}>
        <Paper>
          <Box sx={{ p: 2 }}>
            {restrictGuTeamsForPa ? (
              <Grid
                container
                spacing={2}
                alignItems="center"
                className={styles.personalDetailsHeader}
              >
                <Grid item sm={6}>
                  <p className={styles.formTitle}>Personal Details</p>
                </Grid>
                <Grid item sm={6}>
                  <Grid container spacing={2}>
                    <Grid item sm={6} />
                    <Grid item sm={6}>
                      <p className={styles.formNoteInline}>
                        Note: you may only assign a Global User to teams to which you have access.
                      </p>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            ) : (
              <div className={styles.personalDetailsTitleOnly}>
                <p className={styles.formTitle}>Personal Details</p>
              </div>
            )}
            <Grid container spacing={2}>
              <Grid item sm={6}>
                <Input
                  tabIndex={1}
                  register={register}
                  errors={errors}
                  name="firstName"
                  label="First Name"
                />
              </Grid>
              <Grid item sm={6}>
                <Grid container spacing={2}>
                  <Grid item sm={globalUserSelected ? 6 : 12}>
                    <Select
                      tabIndex={5}
                      options={memberOptions}
                      name="primaryRole"
                      control={control}
                      errors={errors}
                      label="Primary Role"
                      hideSelectedOptions={false}
                      openMenuOnFocus
                    />
                  </Grid>
                  {globalUserSelected && (
                    <Grid item sm={6}>
                      <Select
                        tabIndex={5}
                        isMulti
                        oneLineMulti
                        closeMenuOnSelect={false}
                        showCustomComponents
                        showSelectAll
                        hideSelectedOptions={false}
                        name="teamIds"
                        options={teamOptions}
                        control={control}
                        errors={errors}
                        label="Assigned Teams"
                        isClearable
                        placeholder="All"
                        setAll={setAll}
                        isOptionDisabled={
                          restrictGuTeamsForPa ? isTeamOptionDisabledForPa : undefined
                        }
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item sm={6}>
                <Input
                  tabIndex={2}
                  register={register}
                  errors={errors}
                  name="lastName"
                  label="Last Name"
                />
              </Grid>
              <Grid item sm={6}>
                <Select
                  tabIndex={6}
                  isMulti
                  control={control}
                  errors={errors}
                  name="subRoleIds"
                  label="Sub-Role"
                  options={options.subRoles}
                  hideSelectedOptions={false}
                  openMenuOnFocus
                />
              </Grid>
              <Grid item sm={6}>
                <InputMask
                  tabIndex={3}
                  control={control}
                  errors={errors}
                  name="phone"
                  label="Phone"
                />
              </Grid>
              <Grid item sm={6}>
                <Input
                  tabIndex={7}
                  register={register}
                  errors={errors}
                  name="address.city"
                  label="City"
                />
              </Grid>
              <Grid item sm={6}>
                <div className={styles.combinedInputWrapper}>
                  <Input
                    combinedInput
                    tabIndex={4}
                    register={register}
                    errors={errors}
                    name="email"
                    label="Email"
                  />
                  <SelectEmailDomain
                    name="emailDomain"
                    control={control}
                    errors={errors}
                    options={emailDomainOptions}
                  />
                </div>
              </Grid>
              <Grid item sm={6}>
                <Input
                  tabIndex={8}
                  register={register}
                  errors={errors}
                  name="address.line1"
                  label="Address 1"
                />
              </Grid>
              <Grid item sm={6}>
                <AvatarUpload control={control} name="photo" errors={errors} isPhoto />
              </Grid>
              <Grid item sm={6}>
                <Input
                  tabIndex={9}
                  register={register}
                  errors={errors}
                  name="address.line2"
                  label="Address 2"
                />
              </Grid>
              <Grid item sm={6} className={styles.col} />
              <Grid item sm={3} className={styles.col}>
                <Select
                  tabIndex={10}
                  options={options.states}
                  name="address.stateId"
                  control={control}
                  errors={errors}
                  label="State"
                  hideSelectedOptions={false}
                  openMenuOnFocus
                />
              </Grid>
              <Grid item sm={3} className={styles.col}>
                <InputMask
                  tabIndex={11}
                  alwaysShowMask={false}
                  mask="99999"
                  name="address.zipCode"
                  control={control}
                  errors={errors}
                  label="Zip Code"
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>
        <div className={styles.paperContainer}>
          <Paper>
            <Box sx={{ p: 2 }}>
              <p className={styles.formTitle}>Work Details</p>
              <Grid container spacing={2}>
                <Grid item sm={6}>
                  <Select
                    tabIndex={12}
                    options={options.credentials}
                    control={control}
                    errors={errors}
                    name="workDetails.credentialsId"
                    label="Credentials"
                    hideSelectedOptions={false}
                    openMenuOnFocus
                  />
                </Grid>
                <Grid item sm={6}>
                  <Select
                    tabIndex={15}
                    options={options.employmentStatuses}
                    control={control}
                    errors={errors}
                    name="workDetails.employmentStatusId"
                    label="Employment Status"
                    hideSelectedOptions={false}
                    openMenuOnFocus
                  />
                </Grid>
                <Grid item sm={6}>
                  <Select
                    tabIndex={13}
                    options={options.educations}
                    control={control}
                    errors={errors}
                    name="workDetails.educationId"
                    label="Education"
                    hideSelectedOptions={false}
                    openMenuOnFocus
                  />
                </Grid>
                <Grid item sm={6}>
                  <Input
                    tabIndex={16}
                    type="number"
                    register={register}
                    errors={errors}
                    name="workDetails.experience"
                    label="Years of Experience"
                  />
                </Grid>
                <Grid item sm={6}>
                  <DatePicker
                    tabIndex={14}
                    control={control}
                    openTo="date"
                    errors={errors}
                    label="Hire Date"
                    name="workDetails.hireDate"
                  />
                </Grid>
                <Grid item sm={6}>
                  <Input
                    tabIndex={17}
                    register={register}
                    errors={errors}
                    name="emrId"
                    label="EMR ID"
                  />
                </Grid>
              </Grid>
            </Box>
          </Paper>
          <div className={styles.buttonsWrapper}>
            <div className={styles.buttonWrapper}>
              <Link to={cancelLinkTo} className={styles.link}>
                <Button variant="outlined" color="primary" disabled={isProcessing}>
                  Cancel
                </Button>
              </Link>
            </div>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              isLoading={isProcessing}
              disabled={isProcessing || Object.keys(errors).length > 0}
              tabIndex={17}
            >
              Send Invite
            </Button>
          </div>
        </div>
      </form>
      <NavigationConfirmModal when={isDirty && !isProcessing} description={leavePageDescription} />
    </div>
  );
};

export default Form;
